/**
 * CalendarCardModal — the Adviser-only detail modal behind a review
 * calendar card on the dashboard pipeline (CLAUDE.md §34.9 / §34.10).
 * Patterned after the prep-request detail (compact single-purpose
 * surface), NOT FileDetail.
 *
 * Works on a card UNIT: one ungrouped client, or a whole group. For a
 * group the header reads "«label» — «N» clients · «Month» review" and
 * each member gets its own bordered checklist section (entities show
 * only Annual Review + Commission Splits, §34.10). Checklist ticks are
 * per-member (optimistic, immediate); STATUS transitions apply to ALL
 * members atomically via the group-expanding API.
 *
 * Transitions (forward only — no backwards transitions):
 *   submitted → appointment_made   "Appointment made"
 *   appointment_made → completed   "Mark review completed" — GATED on
 *     EVERY member's applicable checklist items being ticked; when
 *     blocked, the outstanding items are listed PER MEMBER and nothing
 *     is written.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarCheck, AlertTriangle, Building2, Notebook, CheckCircle2 } from 'lucide-react';
import {
  MONTH_NAMES,
  REVIEW_STATUS_LABELS,
  applicableChecklistItems,
  getOutstandingChecklistItems,
} from '../../utils/reviewPlanner';
import { updateReviewClient, updateReviewClientsExpanded } from '../../api/reviewClientsApi';
import { formatDateTime } from '../../utils/dateHelpers';
import CreatePrepFromReviewModal from './CreatePrepFromReviewModal';

const STATUS_CHIP_STYLES = {
  submitted: { background: 'rgba(27, 154, 170, 0.12)', color: 'var(--cp-secondary)' },
  appointment_made: { background: 'rgba(244, 162, 97, 0.18)', color: 'var(--cp-accent-dark)' },
  completed: { background: 'rgba(46, 196, 182, 0.15)', color: 'var(--cp-success)' },
};

export default function CalendarCardModal({ card, onClose, onUpdated, onToast }) {
  const navigate = useNavigate();
  // Local working copy of the unit's members so checklist ticks and
  // transitions render instantly (optimistic) and reconcile with the
  // Supabase writes; onUpdated pushes confirmed rows up to the
  // dashboard's reviewClients list.
  const [members, setMembers] = useState(card.members);
  const [gateWarning, setGateWarning] = useState(null); // [{name, items}]
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Group prep-handoff modal (§34.11).
  const [showCreatePrep, setShowCreatePrep] = useState(false);

  if (!members || members.length === 0) return null;

  const anchor = members[0]; // shared status/timestamps by invariant
  const isGroup = card.isGroup;
  const title = isGroup
    ? `${card.label} — ${members.length} clients · ${MONTH_NAMES[anchor.reviewMonth]} review`
    : card.label;

  /** Per-member checklist tick — never propagates across the group. */
  const toggleItem = async (member, item, checked) => {
    setGateWarning(null); // ticking anything invalidates the old warning
    const revert = { ...member };
    const patch = { [item.doneField]: checked };
    setMembers((prev) => prev.map((m) => (m.id === member.id ? { ...m, ...patch } : m)));
    setError('');
    try {
      const saved = await updateReviewClient(member.id, patch);
      setMembers((prev) => prev.map((m) => (m.id === member.id ? saved : m)));
      onUpdated && onUpdated([saved]);
    } catch (err) {
      setMembers((prev) => prev.map((m) => (m.id === member.id ? revert : m)));
      setError(err.message || 'Could not save the change.');
    }
  };

  /** Shared transition — applied to every member atomically (§34.10). */
  const applyTransition = async (patch) => {
    setSaving(true);
    setError('');
    try {
      const saved = await updateReviewClientsExpanded(
        members.map((m) => m.id),
        patch
      );
      const byId = {};
      saved.forEach((r) => { byId[r.id] = r; });
      setMembers((prev) => prev.map((m) => byId[m.id] || m));
      onUpdated && onUpdated(saved);
      setSaving(false);
      return true;
    } catch (err) {
      setError(err.message || 'Could not save the change.');
      setSaving(false);
      return false;
    }
  };

  const handleAppointmentMade = async () => {
    const ok = await applyTransition({
      status: 'appointment_made',
      appointmentMadeAt: new Date().toISOString(),
    });
    if (ok) onToast && onToast(`Appointment made — ${card.label}`);
  };

  // ---- Phase 3 prep handoff (§34.11) ----
  // Linked once any member carries prep_request_id (shared across a
  // group by invariant). One meeting, one request, ever: once linked,
  // the create affordance is permanently replaced — including after
  // completion.
  const linkedPrepId = anchor.prepRequestId || null;
  const handoffVisible = anchor.status === 'appointment_made' || anchor.status === 'completed';

  const handleCreatePrep = () => {
    if (isGroup) {
      // Group: the segmented handoff modal (one request, member sections).
      setShowCreatePrep(true);
    } else {
      // Single client: the EXISTING prep submission flow, pre-filled with
      // the natural display name; linkage is written on submit there.
      navigate('/submit-prep', {
        state: {
          prefillClientName: anchor.displayName,
          sourceReviewClientId: anchor.id,
        },
      });
    }
  };

  const handlePrepCreated = (prep, updatedReviews) => {
    setShowCreatePrep(false);
    const byId = {};
    (updatedReviews || []).forEach((r) => { byId[r.id] = r; });
    setMembers((prev) => prev.map((m) => byId[m.id] || m));
    onUpdated && onUpdated(updatedReviews || []);
  };

  const handleComplete = async () => {
    // Completion gate (§34.10): EVERY member's applicable items must be
    // ticked. When blocked, outstanding items are listed per member and
    // nothing is written.
    const blocked = members
      .map((m) => ({ name: m.displayName, isEntity: m.isEntity, items: getOutstandingChecklistItems(m) }))
      .filter((b) => b.items.length > 0);
    if (blocked.length > 0) {
      setGateWarning(blocked);
      return;
    }
    setGateWarning(null);
    const ok = await applyTransition({
      status: 'completed',
      completedAt: new Date().toISOString(),
    });
    if (ok) {
      onToast && onToast(`Review completed — ${card.label} 🎉`);
      onClose();
    }
  };

  const timestamps = [
    { label: 'Added to pipeline', value: anchor.submittedAt },
    { label: 'Appointment made', value: anchor.appointmentMadeAt },
    { label: 'Completed', value: anchor.completedAt },
  ].filter((t) => t.value);

  return (
    <div className="rv-overlay" onClick={onClose}>
      <div className="rv-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Review calendar card">
        <h3 className="rv-modal-title" style={{ marginBottom: '0.35rem' }}>
          <CalendarCheck size={18} />
          {!isGroup && anchor.isEntity && <Building2 size={16} title="Entity" />}
          {title}
        </h3>
        {!isGroup && (
          <div style={{ fontSize: '0.8rem', color: 'var(--cp-text-muted)', marginBottom: '0.9rem' }}>
            {MONTH_NAMES[anchor.reviewMonth]} {anchor.reviewYear} review
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', margin: '0.5rem 0 1rem', flexWrap: 'wrap' }}>
          <span
            className="rv-status-chip"
            style={STATUS_CHIP_STYLES[anchor.status] || STATUS_CHIP_STYLES.submitted}
          >
            {REVIEW_STATUS_LABELS[anchor.status] || anchor.status}
          </span>
        </div>

        {timestamps.length > 0 && (
          <div className="rv-field" style={{ marginBottom: '0.9rem' }}>
            {timestamps.map((t) => (
              <div key={t.label} style={{ fontSize: '0.74rem', color: 'var(--cp-text-muted)', lineHeight: 1.6 }}>
                {t.label}: <strong style={{ color: 'var(--cp-text)' }}>{formatDateTime(t.value)}</strong>
              </div>
            ))}
          </div>
        )}

        {error && <div className="rv-error">{error}</div>}

        {/* Prep handoff (§34.11): create affordance while unlinked; the
            permanent linked state once the meeting's ONE request exists. */}
        {handoffVisible && (
          <div className="rv-field" style={{ marginBottom: '1rem' }}>
            {linkedPrepId ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', fontSize: '0.8rem', color: 'var(--cp-success)', fontWeight: 600 }}>
                <CheckCircle2 size={15} />
                <span>Prep request created</span>
                <button
                  className="rv-btn rv-btn-outline"
                  style={{ padding: '0.25rem 0.6rem', fontSize: '0.72rem' }}
                  onClick={() => navigate(`/prep/${linkedPrepId}`)}
                >
                  Open request →
                </button>
              </div>
            ) : anchor.status === 'appointment_made' ? (
              <button className="rv-btn rv-btn-outline" onClick={handleCreatePrep} disabled={saving}>
                <Notebook size={14} /> Create preparation request
              </button>
            ) : null}
          </div>
        )}

        {/* One bordered checklist section per member (a single-client
            card has exactly one). Entities: AR + CS only (§34.10). */}
        {members.map((member) => (
          <div className="rv-field" key={member.id} style={{ marginBottom: '0.75rem' }}>
            <label className="rv-label" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              {isGroup ? member.displayName : 'Checklist'}
              {member.isEntity && <span className="rv-tag rv-tag-entity">ENTITY</span>}
            </label>
            <div style={{ border: '1px solid var(--cp-border)', borderRadius: 'var(--radius)', padding: '0 0.5rem' }}>
              {applicableChecklistItems(member).map((item) => (
                <div key={item.key} className="rv-check-row">
                  <span className="rv-check-label">{item.label}</span>
                  <input
                    type="checkbox"
                    className="rv-checkbox"
                    checked={member[item.doneField]}
                    disabled={anchor.status === 'completed'}
                    onChange={(e) => toggleItem(member, item, e.target.checked)}
                    aria-label={`${member.displayName}: ${item.label} done`}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}

        {gateWarning && (
          <div className="rv-error" style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
            <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: '1px' }} />
            <div>
              <div style={{ fontWeight: 700 }}>Cannot complete — outstanding:</div>
              {gateWarning.map((b) => (
                <div key={b.name}>{b.name}: {b.items.join(', ')}</div>
              ))}
            </div>
          </div>
        )}

        <div className="rv-modal-actions">
          <button className="rv-btn rv-btn-outline" onClick={onClose} disabled={saving}>
            Close
          </button>
          {anchor.status === 'submitted' && (
            <button className="rv-btn rv-btn-primary" onClick={handleAppointmentMade} disabled={saving}>
              {saving ? 'Saving…' : 'Appointment made'}
            </button>
          )}
          {anchor.status === 'appointment_made' && (
            <button className="rv-btn rv-btn-primary" onClick={handleComplete} disabled={saving}>
              {saving ? 'Saving…' : 'Mark review completed'}
            </button>
          )}
        </div>

        {/* Group prep-handoff modal (§34.11) — nothing is created until
            its Send; Cancel just closes it. */}
        {showCreatePrep && (
          <CreatePrepFromReviewModal
            card={{ ...card, members }}
            onClose={() => setShowCreatePrep(false)}
            onCreated={handlePrepCreated}
            onToast={onToast}
          />
        )}
      </div>
    </div>
  );
}
